import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import GestorLayout from '../../components/GestorLayout';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Download,
  FileText,
  Package,
  Clock,
  Target,
  Calendar,
  TrendingUp,
  User
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RelatorioOperador() {
  const { operadorId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [relatorio, setRelatorio] = useState(null);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  useEffect(() => {
    // Definir datas padrão (últimos 30 dias)
    const hoje = new Date();
    const trintaDiasAtras = new Date(hoje);
    trintaDiasAtras.setDate(hoje.getDate() - 30);
    
    setDataFim(hoje.toISOString().split('T')[0]);
    setDataInicio(trintaDiasAtras.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (dataInicio && dataFim && operadorId) {
      loadRelatorio();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operadorId, dataInicio, dataFim]);

  const loadRelatorio = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/operadores/${operadorId}/relatorio`, {
        params: { data_inicio: dataInicio, data_fim: dataFim }
      });
      setRelatorio(response.data);
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
      toast.error('Erro ao carregar relatório do operador');
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/operadores/${operadorId}/relatorio/pdf`, {
        params: { data_inicio: dataInicio, data_fim: dataFim },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Relatorio_${relatorio?.operador?.nome?.replace(/\s+/g, '_') || 'Operador'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.error('Erro ao exportar PDF');
    } finally {
      setExporting(false);
    }
  };

  const getCorClass = (cor) => {
    switch (cor) {
      case 'azul': return 'bg-blue-500';
      case 'verde': return 'bg-green-500';
      case 'amarelo': return 'bg-yellow-500';
      case 'vermelho': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  const getCorBgClass = (cor) => {
    switch (cor) {
      case 'azul': return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'verde': return 'bg-green-50 border-green-200 text-green-800';
      case 'amarelo': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'vermelho': return 'bg-red-50 border-red-200 text-red-800';
      default: return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  if (loading) {
    return (
      <GestorLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </GestorLayout>
    );
  }

  if (!relatorio) {
    return (
      <GestorLayout>
        <div className="px-8 pt-4 pb-8">
          <div className="text-center py-12">
            <p className="text-stone-500">Operador não encontrado</p>
            <Button onClick={() => navigate('/gestor/relatorios')} className="mt-4">
              Voltar
            </Button>
          </div>
        </div>
      </GestorLayout>
    );
  }

  return (
    <GestorLayout>
      <div className="px-8 pt-4 pb-8" data-testid="relatorio-operador-page">
        {/* Cabeçalho */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/gestor/relatorios')}
              className="rounded-full"
              data-testid="btn-voltar"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-3xl font-heading font-bold text-stone-900 flex items-center gap-3">
                <User className="h-8 w-8" />
                {relatorio.operador.nome}
              </h1>
              <p className="text-stone-600 capitalize">
                {relatorio.operador.categoria} • {relatorio.operador.username}
              </p>
            </div>
          </div>
          
          <Button
            onClick={exportPDF}
            disabled={exporting}
            className="rounded-full"
            data-testid="btn-export-pdf"
          >
            <FileText className="h-4 w-4 mr-2" />
            {exporting ? 'A exportar...' : 'Exportar PDF'}
          </Button>
        </div>

        {/* Filtros de Data */}
        <Card className="p-4 mb-6">
          <div className="flex items-center gap-4">
            <Calendar className="h-5 w-5 text-stone-400" />
            <div className="flex items-center gap-2">
              <label className="text-sm text-stone-600">De:</label>
              <input
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
                data-testid="input-data-inicio"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-stone-600">Até:</label>
              <input
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
                data-testid="input-data-fim"
              />
            </div>
          </div>
        </Card>

        {/* Métricas Resumo */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <Card className="p-4 text-center">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <p className="text-2xl font-bold text-stone-900">{relatorio.resumo.total_registos}</p>
            <p className="text-xs text-stone-500">Registos</p>
          </Card>

          <Card className="p-4 text-center">
            <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-2">
              <Package className="h-5 w-5 text-success" />
            </div>
            <p className="text-2xl font-bold text-stone-900">{relatorio.resumo.total_quantidade}</p>
            <p className="text-xs text-stone-500">Peças</p>
          </Card>

          <Card className={`p-4 text-center border-2 ${getCorBgClass(relatorio.resumo.cor)}`}>
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center mx-auto mb-2">
              <Target className="h-5 w-5" />
            </div>
            <p className="text-2xl font-bold">{relatorio.resumo.eficiencia_media}%</p>
            <p className="text-xs opacity-75">Eficiência</p>
          </Card>

          <Card className="p-4 text-center">
            <div className="w-10 h-10 rounded-full bg-info/10 flex items-center justify-center mx-auto mb-2">
              <Clock className="h-5 w-5 text-info" />
            </div>
            <p className="text-2xl font-bold text-stone-900">{relatorio.resumo.total_tempo_produtivo_horas}h</p>
            <p className="text-xs text-stone-500">T. Produtivo</p>
          </Card>

          <Card className="p-4 text-center">
            <div className="w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center mx-auto mb-2">
              <Clock className="h-5 w-5 text-warning" />
            </div>
            <p className="text-2xl font-bold text-stone-900">{relatorio.resumo.total_tempo_pausa_horas}h</p>
            <p className="text-xs text-stone-500">T. Pausa</p>
          </Card>

          <Card className="p-4 text-center">
            <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-2">
              <Calendar className="h-5 w-5 text-stone-600" />
            </div>
            <p className="text-2xl font-bold text-stone-900">{relatorio.resumo.dias_trabalhados}</p>
            <p className="text-xs text-stone-500">Dias Trab.</p>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Desempenho por Operação */}
          <Card className="p-6" data-testid="por-operacao">
            <h2 className="text-lg font-heading font-bold text-stone-900 mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Desempenho por Operação
            </h2>
            
            {relatorio.por_operacao.length === 0 ? (
              <p className="text-stone-500 text-center py-4">Sem dados no período</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-stone-200">
                      <th className="text-left py-2 px-2">Operação</th>
                      <th className="text-right py-2 px-2">Qtd</th>
                      <th className="text-right py-2 px-2">Tempo</th>
                      <th className="text-right py-2 px-2">Efic.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relatorio.por_operacao.map((op, idx) => (
                      <tr key={`${op.codigo || ''}-${op.nome || idx}`} className="border-b border-stone-100">
                        <td className="py-2 px-2">
                          <span className="font-mono text-xs text-stone-400">{op.codigo}</span>
                          <br />
                          <span className="font-medium">{op.nome}</span>
                        </td>
                        <td className="py-2 px-2 text-right">{op.quantidade_produzida}</td>
                        <td className="py-2 px-2 text-right">{op.tempo_horas}h</td>
                        <td className="py-2 px-2 text-right font-semibold">{op.eficiencia_media}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Evolução Diária */}
          <Card className="p-6" data-testid="evolucao-diaria">
            <h2 className="text-lg font-heading font-bold text-stone-900 mb-4 flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Evolução Diária
            </h2>
            
            {relatorio.evolucao_diaria.length === 0 ? (
              <p className="text-stone-500 text-center py-4">Sem dados no período</p>
            ) : (
              <div className="overflow-x-auto max-h-80 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-stone-200">
                      <th className="text-left py-2 px-2">Data</th>
                      <th className="text-right py-2 px-2">Qtd</th>
                      <th className="text-right py-2 px-2">Tempo</th>
                      <th className="text-right py-2 px-2">Efic.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relatorio.evolucao_diaria.map((dia) => (
                      <tr key={dia.data} className="border-b border-stone-100">
                        <td className="py-2 px-2 font-medium">{dia.data}</td>
                        <td className="py-2 px-2 text-right">{dia.quantidade}</td>
                        <td className="py-2 px-2 text-right">{dia.tempo_horas}h</td>
                        <td className="py-2 px-2 text-right font-semibold">{dia.eficiencia}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        {/* Histórico de Registos */}
        <Card className="p-6 mt-6" data-testid="historico">
          <h2 className="text-lg font-heading font-bold text-stone-900 mb-4">
            Histórico de Registos (últimos 50)
          </h2>
          
          {relatorio.historico.length === 0 ? (
            <p className="text-stone-500 text-center py-4">Sem registos no período</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="text-left py-2 px-3">Data</th>
                    <th className="text-left py-2 px-3">OF</th>
                    <th className="text-left py-2 px-3">Operação</th>
                    <th className="text-right py-2 px-3">Qtd</th>
                    <th className="text-right py-2 px-3">Tempo (min)</th>
                    <th className="text-right py-2 px-3">Eficiência</th>
                  </tr>
                </thead>
                <tbody>
                  {relatorio.historico.map((reg, idx) => (
                    <tr key={`${reg.data}-${reg.of}-${reg.operacao}-${idx}`} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-2 px-3">{reg.data}</td>
                      <td className="py-2 px-3 font-mono">{reg.of}</td>
                      <td className="py-2 px-3">{reg.operacao}</td>
                      <td className="py-2 px-3 text-right">{reg.quantidade}</td>
                      <td className="py-2 px-3 text-right">{reg.tempo_minutos}</td>
                      <td className="py-2 px-3 text-right font-semibold">{reg.eficiencia}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </GestorLayout>
  );
}
